import styles from "./VideoMenu.module.css";
import {
    createStyles,
    createTheme,
    IconButton,
    makeStyles,
    Modal,
    Theme,
    ThemeProvider,
} from "@material-ui/core";
import Menu, { SubMenu, Item as MenuItem } from "rc-menu";
import React, { useState, useCallback, memo, useEffect } from "react";
import "./rc-menu.css";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../root/RootReducer";
import { isAdmin, isEditor } from "../../../utils/LogicUtils";
import { AddVideoToProgram, GetAllProgram } from "../../../stores/program/ProgramActions";
import PuiMessage from "../../../components/widgets/PuiMessage";
import PuiTypography from "../../../components/elements/PuiTypography";
import ClearIcon from "@material-ui/icons/Clear";
import { IsDevice } from "../../../components/function/useDeviceFlag";
import RecVideoSwitch from "./RecVideoSwitch";
import { Video } from "../../video/models/Video";
import { t } from "i18next";

interface VideoMenuProps {
    video: Video;
    videoId: number;
    setMsg: (msg: string) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        formSelect: {
            border: "1px solid #606060",
            width: IsDevice("iphone") ? "100%" : "440px",
            height: "40px",
            fontSize: "14px",
            background: "#FFFFFF0D",
            borderRadius: "5px",
            fontFamily: "Yu Gothic",
            "& .MuiSelect-icon": {
                color: "white",
            },
            "& .MuiSelect-selectMenu": {
                color: "white",
            },
        },
        icon: {
            width: "178px",
            height: "32px",
            paddingTop: "4px",
            paddingLeft: "20px",
            backgroundColor: "#1E2329",
            "&:hover": {
                backgroundColor: "#35393F",
            },
        },
    })
);

const theme = createTheme({
    palette: {
        secondary: {
            main: "#16ADC1",
        },
    },
});

function VideoMenu({ videoId, setMsg, video }: VideoMenuProps) {
    const classes = useStyles();
    const dispatch = useDispatch();

    const { user, userInfo } = useSelector((state: AppState) => ({
        userInfo: state.user.userInfo,
        user: state.user.user,
    }));

    const { allProgramList } = useSelector(({ program }: AppState) => program);

    const [showMenu, setShowMenu] = useState<boolean>(false);

    // ビデオ管理者編集モーダル画面表示制御
    const [isOpenAdminEditVideoModal, ShowAdminEditVideoModal] = useState(false);

    // ビデオ管理者編集モーダル非表示
    const CloseAdminEditVideoModal = () => {
        ShowAdminEditVideoModal(false);
    };

    const OpenAdminEditVideoModal = () => {
        ShowAdminEditVideoModal(true);
    };

    useEffect(() => {
        if (showMenu === true) {
            if (allProgramList.length === 0) {
                if (userInfo) dispatch(GetAllProgram(userInfo.id));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showMenu]);

    const handleOuterClick = useCallback(() => {
        setShowMenu(false);
        document.removeEventListener("click", handleOuterClick);
    }, []);

    const handleMenuIcon = useCallback(
        (toSet: boolean) => () => {
            document.dispatchEvent(new Event("click"));

            setShowMenu(toSet);

            if (toSet) {
                document.addEventListener("click", handleOuterClick);
            } else {
                document.removeEventListener("click", handleOuterClick);
            }
        },
        [setShowMenu, handleOuterClick]
    );

    const handlePropagation = useCallback((event: React.MouseEvent) => {
        event.nativeEvent.stopImmediatePropagation();
    }, []);

    const [isDuplicatedEpisode, setIsDuplicatedEpisode] = useState(false);
    const [isDuplicatedRelateVideo, setIsDuplicatedRelateVideo] = useState(false);

    const CloseDuplicatedEpisodeBar = () => {
        setIsDuplicatedEpisode(false);
    };
    const CloseDuplicatedRelatedBar = () => {
        setIsDuplicatedRelateVideo(false);
    };

    const handleAddToProgram = (title: string, id: string) => {
        setShowMenu(false);
        if (title.includes("episode")) {
            let targetVideos = allProgramList.find((pro) => {
                return pro.id === id;
            });
            let dupVideo = targetVideos?.videos.find((el) => {
                return el.id.toString() === videoId.toString();
            });
            if (userInfo && !dupVideo) {
                setMsg(title.split("episode").join("エピソード"));
                dispatch(AddVideoToProgram(userInfo.id, id, videoId.toString(), 1));
            } else {
                setIsDuplicatedEpisode(true);
            }
        } else if (title.includes("relationVideo")) {
            let targetVideos = allProgramList.find((pro) => {
                return pro.id === id;
            });
            let dupRelatedVideo = targetVideos?.relatedVideos.find((el) => {
                return el.id.toString() === videoId.toString();
            });
            if (userInfo && !dupRelatedVideo) {
                setMsg(title.split("relationVideo").join("関連するVideo"));
                dispatch(AddVideoToProgram(userInfo.id, id, videoId.toString(), 2));
            } else {
                setIsDuplicatedRelateVideo(true);
            }
        }
    };

    return (
        <>
            {(isAdmin(user?.roles) || isEditor(user?.roles)) && (
                <div className={styles.menuArea} onClick={handlePropagation}>
                    <div className={classes.icon} onClick={OpenAdminEditVideoModal}>
                        <PuiTypography size={14}> {t("カテゴリー追加")}</PuiTypography>
                    </div>
                </div>
            )}
            <Modal
                className={styles.editVideoAdminModal}
                open={isOpenAdminEditVideoModal}
                onClose={CloseAdminEditVideoModal}
            >
                <div
                    className={styles.root}
                    style={{
                        width: IsDevice("iphone") ? "91vw" : "",
                    }}
                >
                    {IsDevice("desktop") && (
                        <IconButton onClick={CloseAdminEditVideoModal}>
                            <div className={styles.closeIcon}>
                                <ClearIcon />
                            </div>
                        </IconButton>
                    )}

                    <div className={styles.titleText}>
                        <PuiTypography size={18}> {t("カテゴリー追加")}</PuiTypography>
                    </div>
                    <ThemeProvider theme={theme}>
                        {!IsDevice("desktop") && (
                            <div onClick={CloseAdminEditVideoModal} className={styles.spCloseIcon}>
                                <img
                                    src={require("../../../assets/image/closeFilter.svg").default}
                                    alt="close"
                                />
                            </div>
                        )}
                        <ul className={styles.modalUl}>
                            <li className={styles.list}>
                                <li className={styles.modalTitle}> {t("チャンネル登録")}</li>
                                <div className={styles.form}>
                                    <div
                                        onClick={handleMenuIcon(!showMenu)}
                                        className={classes.formSelect}
                                    >
                                        <div className={styles.itemText}>
                                            <PuiTypography size={14}>{t("選択なし")}</PuiTypography>
                                        </div>
                                        <div className={styles.itemText}></div>
                                        <Menu
                                            className={showMenu ? styles.menu : styles.nomenu}
                                            selectable={false}
                                        >
                                            {allProgramList.length > 0 &&
                                                allProgramList.map((item) => (
                                                    <SubMenu title={item.title} key={item.id}>
                                                        <MenuItem
                                                            key={`Program-${item.id}-episode`}
                                                            onClick={() =>
                                                                handleAddToProgram(
                                                                    `Program-${item.title}-episode`,
                                                                    item.id
                                                                )
                                                            }
                                                        >
                                                            {t("エピソード")}
                                                        </MenuItem>
                                                        <MenuItem
                                                            key={`Program-${item.id}-relationVideo`}
                                                            onClick={() =>
                                                                handleAddToProgram(
                                                                    `Program-${item.title}-relationVideo`,
                                                                    item.id
                                                                )
                                                            }
                                                        >
                                                            {t("関連するVideo")}
                                                        </MenuItem>
                                                    </SubMenu>
                                                ))}
                                            {allProgramList.length === 0 && (
                                                <MenuItem key="noProgramData" disabled>
                                                    {t("チャンネルがありません")}
                                                </MenuItem>
                                            )}
                                        </Menu>
                                    </div>
                                </div>
                            </li>
                            <li className={styles.modalTitle}>
                                <RecVideoSwitch videoId={videoId} setMsg={setMsg} />
                            </li>
                        </ul>
                    </ThemeProvider>
                    <PuiMessage
                        open={isDuplicatedEpisode}
                        type="error"
                        title={t("このVideoは既に選択したChannelのエピソードに登録されています")}
                        duration={5000}
                        onClose={CloseDuplicatedEpisodeBar}
                    />
                    <PuiMessage
                        open={isDuplicatedRelateVideo}
                        type="error"
                        title={t("このVideoは既に選択したChannelの関連Videoに登録されています")}
                        duration={5000}
                        onClose={CloseDuplicatedRelatedBar}
                    />
                </div>
            </Modal>
        </>
    );
}

export default memo(VideoMenu);
